import {
  FEATURES,
  GROUP_TYPE,
  IForwardToFilters,
  IForwardToLoadingStates,
  IRecentContacts,
  USER_TYPE,
} from "@home-health-notify/domain";
import GroupsIcon from "@mui/icons-material/Groups";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import { OrganizationContext } from "hhn-commons";
import * as React from "react";
import { Dispatch, SetStateAction, useContext } from "react";
import { MenuBarTabsContainer } from "../../../chats/ChatListPanel.style";
import TabPanel from "../../../common/tabs/TabPanel";
import {
  ForwardToPanelWrapper,
  ForwardToTabItem,
  ForwardToTabsDivider,
} from "../MessagePanel.style";
import {
  ForwardToContent as ForwardToPatientChats,
  ForwardToContent as ForwardToInternalChats,
  ForwardToContent as ForwardToDirectChats,
} from "./ForwardToContent";
import ForwardToPanelChips from "./ForwardToPanelChips";
import {
  ForwardToPanelChipsDivider,
  ForwardToPanelChipsWrapper,
} from "./ForwardToPanelChips.style";

// Only the active panel gets the flex-fill sx — applying it unconditionally
// would override TabPanel's `hidden` attribute and show all 3 at once.
const forwardToPanelSx = (isActive: boolean) =>
  isActive
    ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
    : undefined;

interface ForwardToPanelProps {
  forwardToList: IRecentContacts[];
  isLoading: IForwardToLoadingStates;
  selectedContactsToForwardTo: IRecentContacts[];
  setSelectedContactsToForwardTo: Dispatch<SetStateAction<IRecentContacts[]>>;
  fetchRecentContacts: (
    filters?: IForwardToFilters,
    paginate?: boolean,
  ) => Promise<void>;
  onSearchTextChange: (searchText: string) => void;
  onSearchClear: () => void;
  filters: IForwardToFilters;
  tabValue: number;
  handleTabChange: (_event: React.SyntheticEvent, newValue: number) => void;
}

function ForwardToPanel({
  forwardToList,
  isLoading,
  selectedContactsToForwardTo = [],
  setSelectedContactsToForwardTo,
  fetchRecentContacts,
  onSearchTextChange,
  onSearchClear,
  filters,
  tabValue,
  handleTabChange,
}: ForwardToPanelProps) {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const selectedRole = selectedOrgUser?.getOrganization().userProfile.type;
  const externalUsers =
    selectedRole === USER_TYPE.PATIENT || selectedRole === USER_TYPE.PHYSICIAN;
  const allTabs = [
    {
      index: 0,
      icon: <HomeIcon fontSize="large" />,
      groupType: GROUP_TYPE.PATIENT,
      visible: true,
      dataTestId: "forward-to-patient-chat-list-tab",
    },
    {
      index: 1,
      icon: <GroupsIcon fontSize="large" />,
      groupType: GROUP_TYPE.NON_PATIENT,
      visible: !!selectedOrgUser?.hasAccess(FEATURES.VIEW_INTERNAL_GROUPS),
      dataTestId: "forward-to-internal-chat-list-tab",
    },
    {
      index: 2,
      icon: <PersonIcon fontSize="large" />,
      groupType: GROUP_TYPE.PRIVATE,
      visible: !externalUsers,
      dataTestId: "forward-to-direct-chat-list-tab",
    },
  ];

  return (
    <>
      <ForwardToPanelWrapper>
        <MenuBarTabsContainer
          slotProps={{
            indicator: { style: { display: "none" } },
          }}
          value={tabValue}
          onChange={handleTabChange}
          aria-label="tabs in forward to panel"
          variant="fullWidth"
        >
          {allTabs
            .filter(tab => tab.visible)
            .map(tab => (
              <ForwardToTabItem
                icon={tab.icon}
                key={tab.index}
                data-testid={tab.dataTestId}
              />
            ))}
        </MenuBarTabsContainer>
      </ForwardToPanelWrapper>
      <ForwardToTabsDivider />
      <TabPanel
        value={tabValue}
        index={0}
        uniqueKey="forward-to-patient-chats"
        sx={forwardToPanelSx(tabValue === 0)}
      >
        <ForwardToPatientChats
          forwardToList={forwardToList}
          isLoading={isLoading}
          selectedContactsToForwardTo={selectedContactsToForwardTo}
          setSelectedContactsToForwardTo={setSelectedContactsToForwardTo}
          fetchRecentContacts={fetchRecentContacts}
          onSearchTextChange={onSearchTextChange}
          onSearchClear={onSearchClear}
          filters={filters}
          supportsMrnSearch
        />
      </TabPanel>
      <TabPanel
        value={tabValue}
        index={1}
        uniqueKey="forward-to-internal-chats"
        sx={forwardToPanelSx(tabValue === 1)}
      >
        <ForwardToInternalChats
          forwardToList={forwardToList}
          isLoading={isLoading}
          selectedContactsToForwardTo={selectedContactsToForwardTo}
          setSelectedContactsToForwardTo={setSelectedContactsToForwardTo}
          fetchRecentContacts={fetchRecentContacts}
          onSearchTextChange={onSearchTextChange}
          onSearchClear={onSearchClear}
          filters={filters}
        />
      </TabPanel>
      <TabPanel
        value={tabValue}
        index={2}
        uniqueKey="forward-to-direct-chats"
        sx={forwardToPanelSx(tabValue === 2)}
      >
        <ForwardToDirectChats
          forwardToList={forwardToList}
          isLoading={isLoading}
          selectedContactsToForwardTo={selectedContactsToForwardTo}
          setSelectedContactsToForwardTo={setSelectedContactsToForwardTo}
          fetchRecentContacts={fetchRecentContacts}
          onSearchTextChange={onSearchTextChange}
          onSearchClear={onSearchClear}
          filters={filters}
        />
      </TabPanel>
      {selectedContactsToForwardTo.length > 0 && (
        <ForwardToPanelChipsWrapper>
          <ForwardToPanelChipsDivider />
          <ForwardToPanelChips
            selectedContacts={selectedContactsToForwardTo}
            onRemoveContact={id =>
              setSelectedContactsToForwardTo(prev =>
                prev.filter(c => c.id !== id),
              )
            }
          />
        </ForwardToPanelChipsWrapper>
      )}
    </>
  );
}

export default ForwardToPanel;
